export default () => {
  class FileWithURL extends File {
    objectURL: string;

    constructor(file: File, objectURL: string) {
      super([file], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
      this.objectURL = objectURL;
    }
  }

  const createImageElement = (file: File | FileWithURL): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const imgUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(imgUrl);
        resolve(img);
      };

      img.onerror = () => {
        reject(new Error('Image loading error.'));
      };

      img.src = imgUrl;
    });
  };
  const createCanvas = (img: HTMLImageElement, height: number, width: number) => {
    const canvas = document.createElement('canvas');
    if (!canvas) {
      throw new Error('Canvas not supported');
    }
    const ctx = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    return canvas;
  };

  const compressToBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> => {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
    });
  };
  const getResizeDimensions = (img: HTMLImageElement, maxDimension = 1024) => {
    let { width, height } = img;

    // Check if dimensions are within limits
    if (width <= maxDimension || height <= maxDimension) {
      return { height, width };
    }

    const aspectRatio = width / height;

    // Resize based on the aspect ratio
    if (width > height) {
      // Wider than tall
      width = maxDimension;
      height = maxDimension / aspectRatio;
    } else {
      // Taller than wide
      height = maxDimension;
      width = maxDimension * aspectRatio;
    }

    return { height, width };
  };

  const compressImage = async (file: File | FileWithURL, targetKB = 500): Promise<File> => {
    if (!/image\/(jpeg|png)/.test(file.type)) {
      throw new Error('File must be a JPEG or PNG.');
    }
    if (file.size <= targetKB * 1024) {
      return file;
    }

    const img = await createImageElement(file);
    const { height, width } = getResizeDimensions(img);
    const canvas = createCanvas(img, height, width);

    let quality = 1;
    let compressedBlob: Blob;

    do {
      compressedBlob = await compressToBlob(canvas, quality);
      if (compressedBlob.size <= targetKB * 1024) break;
      quality = Math.max(0, Math.round((quality - 0.1) * 10) / 10); // ffs I hate you floating point
    } while (quality > 0);

    return new File([compressedBlob], file.name.replace(/\.(jpeg|jpg|png)$/i, '.jpg'), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  };

  const generateFileWithUrl = (file: File): FileWithURL => {
    const objectUrl = URL.createObjectURL(file);
    return new FileWithURL(file, objectUrl);
  };

  const generateCompressedImageWithObjectUrl = async (
    file: File,
    targetKB = 500,
  ): Promise<FileWithURL> => {
    const compressedImage = await compressImage(file, targetKB);
    return generateFileWithUrl(compressedImage);
  };

  return {
    compressImage,
    generateCompressedImageWithObjectUrl,
  };
};
